var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
import * as React from 'react';
import { useRef, useState } from 'react';
import { Callout, DirectionalHint, Icon, IconButton, Link, Text } from '@fluentui/react';
import { completionColor, skillsLastUpdatedDisplay } from '../shared/fmEndorsement';
import { exportForecastToCsv } from '../shared/exportForecast';
import { ensureFluentIcons } from '../shared/fmIcons';
import EventLegend from '../Legends/EventLegend';
import EndorsementLegend from '../Legends/EndorsementLegend';
/**
 * FM forecast view (F1 + F2) — React port of the GRM `fmforecastview` screen: a frozen
 * profile-completeness panel (Resource / Profile Completion % / Skill Assessment % / Skills Last
 * Updated / Capacity Hours) joined to a horizontally scrollable month calendar that renders the
 * level-packed event bars, weekend hatching, holidays and a highlighted current day. Includes the
 * Export action and the Event / endorsement legend dropdowns. Presentational + self-dependent:
 * rows/days are laid out upstream (`fmForecastLayout`) and injected, so it renders and is testable
 * without the FXP shell. Capacity Hours opens the DSE editor.
 */
const NAME_W = 192;
const METRIC_W = [86, 86, 92, 92];
const LEFT_W = NAME_W + METRIC_W.reduce((a, b) => a + b, 0);
const COL_W = 36;
const HEADER_H = 140;
const BAND1_H = 46;
const ROW_H = 116;
const BAR_H = 22;
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const C = {
    headerBg: '#d8e2e8',
    border: '#c9d4db',
    cellBorder: '#eef1f3',
    link: '#0078d4',
    todayBg: '#0078d4',
    metric: '#bf7322',
    headerText: '#3b3a39',
    subtle: '#605e5c',
    holidayBg: '#fff4ce',
};
const WEEKEND_BG = 'repeating-linear-gradient(45deg, #f0f5fb, #f0f5fb 2px, #dde9f5 2px, #dde9f5 5px)';
const asDate = (v) => (v instanceof Date ? v : new Date(v));
const num = (v) => (v === undefined || v === null ? 0 : v);
const fmt = (v) => v === undefined || v === null || v === '' ? '--' : String(v);
const cellStyle = (index, color) => ({
    width: METRIC_W[index],
    padding: 12,
    textAlign: 'center',
    fontSize: 13,
    color,
    borderLeft: '1px solid #eef1f3',
});
export const FMForecastView = ({ rows, days, monthLabel, todayDate, eventTypes = [], exportResources, exportMonthDate, onPrevMonth, onNextMonth, onOpenName, onOpenCalendar, nameHref, calendarHref, onOpenDSE, onAddEvent, onExport, onOpenEventLegend, }) => {
    // Fluent renders an EMPTY glyph for unregistered icons — without this the month chevrons,
    // Export and legend toggles are invisible on the FM routes.
    ensureFluentIcons();
    const gridW = days.length * COL_W;
    const [eventLegendOpen, setEventLegendOpen] = useState(false);
    const [endorsementLegendOpen, setEndorsementLegendOpen] = useState(false);
    const eventLegendRef = useRef(null);
    const endorsementRef = useRef(null);
    const rowHeight = (r) => { var _a; return Math.max(ROW_H, (_a = r.height) !== null && _a !== void 0 ? _a : 0); };
    const isWeekend = (d, wk) => (wk !== null && wk !== void 0 ? wk : [0, 6]).indexOf(asDate(d.date).getDay()) > -1;
    const isHoliday = (d, holidays) => !!holidays && holidays.indexOf(asDate(d.date).getDate()) > -1;
    const isToday = (d) => todayDate !== undefined && asDate(d.date).getDate() === todayDate;
    const todayIndex = days.findIndex((d) => isToday(d));
    const handleExport = () => {
        if (onExport) {
            onExport();
            return;
        }
        exportForecastToCsv(exportResources !== null && exportResources !== void 0 ? exportResources : [], exportMonthDate !== null && exportMonthDate !== void 0 ? exportMonthDate : new Date());
    };
    const openEventLegend = () => {
        onOpenEventLegend === null || onOpenEventLegend === void 0 ? void 0 : onOpenEventLegend();
        setEventLegendOpen((v) => !v);
    };
    const metricHeaders = [
        'Profile Completion %',
        'Skill Assessment %',
        'Skills Last Updated',
        'Capacity Hours',
    ];
    return (React.createElement("div", { "data-testid": "fm-forecast-view", role: "table", "aria-label": "FM forecast view", style: {
            display: 'flex',
            width: '100%',
            maxWidth: LEFT_W + gridW + 2,
            border: `1px solid ${C.border}`,
            background: '#fff',
            color: '#242424',
            fontFamily: 'Segoe UI, system-ui, sans-serif',
        } },
        React.createElement("div", { style: { width: LEFT_W, flex: '0 0 auto', borderRight: `1px solid ${C.border}` } },
            React.createElement("div", { style: {
                    height: HEADER_H,
                    display: 'flex',
                    background: C.headerBg,
                    borderBottom: `1px solid ${C.border}`,
                } },
                React.createElement("div", { style: {
                        width: NAME_W,
                        padding: '12px',
                        fontWeight: 600,
                        fontSize: 12,
                        letterSpacing: 0.3,
                        color: C.headerText,
                        borderRight: `1px solid ${C.border}`,
                    } }, "RESOURCE NAME"),
                React.createElement("div", { style: { flex: 1, display: 'flex', flexDirection: 'column' } },
                    React.createElement("div", { style: {
                            height: BAND1_H,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 10px',
                        } },
                        React.createElement(Text, { styles: { root: { fontWeight: 600, fontSize: 12, color: C.headerText } } }, "PROFILE COMPLETENESS"),
                        React.createElement("div", { style: { flex: 1 } }),
                        React.createElement("span", { ref: (el) => (endorsementRef.current = el), style: { display: 'inline-flex' } },
                            React.createElement(IconButton, { iconProps: { iconName: 'BulletedList2' }, ariaLabel: "Profile completeness endorsement legend", title: "Endorsement legend", "data-testid": "fmfv-endorsement-legend-toggle", onClick: () => setEndorsementLegendOpen((v) => !v), styles: { root: { height: 24, width: 24 }, icon: { fontSize: 14, color: C.subtle } } })),
                        endorsementLegendOpen ? (React.createElement(Callout, { target: endorsementRef.current, directionalHint: DirectionalHint.bottomLeftEdge, onDismiss: () => setEndorsementLegendOpen(false), setInitialFocus: true },
                            React.createElement(EndorsementLegend, null))) : null),
                    React.createElement("div", { style: { flex: 1, display: 'flex', borderTop: `1px solid ${C.border}` } }, metricHeaders.map((h, i) => (React.createElement("div", { key: h, style: {
                            width: METRIC_W[i],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '6px',
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.headerText,
                            borderLeft: i ? `1px solid ${C.border}` : 'none',
                        } }, h)))))),
            rows.map((r) => (React.createElement("div", { key: r.rowId, role: "row", "data-testid": `fmfv-row-${r.rowId}`, style: { height: rowHeight(r), display: 'flex', borderBottom: `1px solid ${C.cellBorder}` } },
                React.createElement("div", { style: {
                        width: NAME_W,
                        padding: 12,
                        overflow: 'hidden',
                        borderRight: `1px solid ${C.border}`,
                    } },
                    React.createElement(Link, { href: nameHref === null || nameHref === void 0 ? void 0 : nameHref(r.rowId), target: (nameHref === null || nameHref === void 0 ? void 0 : nameHref(r.rowId)) ? '_blank' : undefined, rel: (nameHref === null || nameHref === void 0 ? void 0 : nameHref(r.rowId)) ? 'noopener noreferrer' : undefined, onClick: (e) => {
                            e.preventDefault();
                            onOpenName === null || onOpenName === void 0 ? void 0 : onOpenName(r.rowId);
                        }, "aria-label": r.name, styles: {
                            root: {
                                display: 'block',
                                fontSize: 14,
                                color: C.link,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                        } }, r.name),
                    (calendarHref === null || calendarHref === void 0 ? void 0 : calendarHref(r.rowId)) ? (React.createElement(Link, { href: calendarHref(r.rowId), target: "_blank", rel: "noopener noreferrer", "data-testid": `fmfv-calendar-${r.rowId}`, title: `${r.name}'s Calendar`, "aria-label": `Open the calendar of ${r.name} in a new tab`, onClick: (e) => {
                            e.preventDefault();
                            onOpenCalendar === null || onOpenCalendar === void 0 ? void 0 : onOpenCalendar(r.rowId);
                        }, styles: { root: { display: 'block', marginTop: 6, fontSize: 13, color: C.link } } }, "Calendar")) : null,
                    React.createElement("div", { style: { marginTop: 8, fontSize: 12, color: '#323130' } },
                        "90 Days: ",
                        React.createElement("b", null, fmt(r.utility1)),
                        "\u00A0\u00A0YTD: ",
                        React.createElement("b", null, fmt(r.utility2)))),
                React.createElement("div", { style: cellStyle(0, completionColor(r.profileCompletionPercentage)) }, num(r.profileCompletionPercentage)),
                React.createElement("div", { style: cellStyle(1, completionColor(r.skillAssessmentPercentage)) }, num(r.skillAssessmentPercentage)),
                React.createElement("div", { style: cellStyle(2) }, skillsLastUpdatedDisplay(r.lastSkillsUpdated, r.endorsedStatus)),
                React.createElement("div", { style: cellStyle(3) },
                    React.createElement(Link, { "data-testid": `fmfv-capacity-${r.rowId}`, "aria-label": `Edit capacity hours for ${r.name}`, onClick: () => onOpenDSE === null || onOpenDSE === void 0 ? void 0 : onOpenDSE(r) }, fmt(r.contractHours))))))),
        React.createElement("div", { style: { flex: 1, overflowX: 'auto' } },
            React.createElement("div", { style: { width: gridW } },
                React.createElement("div", { style: {
                        position: 'relative',
                        height: HEADER_H,
                        background: C.headerBg,
                        borderBottom: `1px solid ${C.border}`,
                    } },
                    todayIndex > -1 ? (React.createElement("div", { "aria-hidden": "true", "data-testid": "fmfv-today-column", style: {
                            position: 'absolute',
                            left: todayIndex * COL_W,
                            top: 0,
                            width: COL_W,
                            height: HEADER_H,
                            background: C.todayBg,
                        } })) : null,
                    React.createElement("div", { style: {
                            position: 'relative',
                            height: BAND1_H,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 8px',
                        } },
                        React.createElement(IconButton, { iconProps: { iconName: 'ChevronLeft' }, ariaLabel: "Previous month", "data-testid": "fmfv-prev-month", onClick: onPrevMonth, styles: { root: { height: 28 } } }),
                        React.createElement(Text, { styles: {
                                root: {
                                    minWidth: 76,
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: C.headerText,
                                },
                            } }, monthLabel),
                        React.createElement(IconButton, { iconProps: { iconName: 'ChevronRight' }, ariaLabel: "Next month", "data-testid": "fmfv-next-month", onClick: onNextMonth, styles: { root: { height: 28 } } }),
                        React.createElement("div", { style: { flex: 1 } }),
                        React.createElement(Link, { "data-testid": "fmfv-export", onClick: handleExport, "aria-label": "Export to Excel", styles: { root: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 } } },
                            React.createElement(Icon, { iconName: "ExcelDocument", styles: { root: { fontSize: 16, color: '#217346' } } }),
                            "Export"),
                        React.createElement("span", { ref: (el) => (eventLegendRef.current = el), style: { marginLeft: 18 } },
                            React.createElement(Link, { "data-testid": "fmfv-event-legend-toggle", onClick: openEventLegend, "aria-label": "Event legend", styles: { root: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 } } },
                                React.createElement(Icon, { iconName: "BulletedList2", styles: { root: { fontSize: 14 } } }),
                                "Event Legend")),
                        eventLegendOpen ? (React.createElement(Callout, { target: eventLegendRef.current, directionalHint: DirectionalHint.bottomRightEdge, onDismiss: () => setEventLegendOpen(false), setInitialFocus: true },
                            React.createElement(EventLegend, { eventTypes: eventTypes }))) : null),
                    React.createElement("div", { style: { position: 'relative', display: 'flex', height: HEADER_H - BAND1_H - 1 } }, days.map((d, i) => {
                        const date = asDate(d.date);
                        const today = isToday(d);
                        return (React.createElement("div", { key: i, title: today ? 'Today' : undefined, "data-testid": today ? 'fmfv-today-header' : undefined, style: {
                                width: COL_W,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                paddingTop: 24,
                                boxSizing: 'border-box',
                                borderLeft: `1px solid ${C.cellBorder}`,
                                background: today ? C.todayBg : undefined,
                                color: today ? '#fff' : C.subtle,
                            } },
                            React.createElement("div", { style: { fontSize: 12 }, "aria-hidden": "true" }, DOW[date.getDay()]),
                            React.createElement("div", { style: { fontSize: 13, fontWeight: 600, marginTop: 2 }, "aria-hidden": "true" }, date.getDate())));
                    }))),
                rows.map((r) => {
                    var _a;
                    return (React.createElement("div", { key: r.rowId, role: "row", style: {
                            position: 'relative',
                            height: rowHeight(r),
                            borderBottom: `1px solid ${C.cellBorder}`,
                        } },
                        React.createElement("div", { style: { display: 'flex', height: '100%' } }, days.map((d, i) => {
                            const cellDate = asDate(d.date);
                            const addable = !!onAddEvent;
                            return (React.createElement("div", { key: i, role: addable ? 'button' : undefined, tabIndex: addable ? 0 : undefined, "aria-label": addable ? `Add event for ${r.name} on ${cellDate.toDateString()}` : undefined, title: addable ? 'Add event' : undefined, "data-testid": addable ? `fmfv-addcell-${r.rowId}-${i}` : undefined, onClick: addable ? () => onAddEvent === null || onAddEvent === void 0 ? void 0 : onAddEvent(r, cellDate) : undefined, onKeyDown: addable
                                    ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onAddEvent === null || onAddEvent === void 0 ? void 0 : onAddEvent(r, cellDate);
                                        }
                                    }
                                    : undefined, style: {
                                    width: COL_W,
                                    height: '100%',
                                    boxSizing: 'border-box',
                                    borderLeft: `1px solid ${C.cellBorder}`,
                                    cursor: addable ? 'pointer' : undefined,
                                    background: isHoliday(d, r.publicHolidays)
                                        ? C.holidayBg
                                        : isWeekend(d, r.weekends)
                                            ? WEEKEND_BG
                                            : undefined,
                                } }));
                        })),
                        ((_a = r.lines) !== null && _a !== void 0 ? _a : []).map((line, i) => (React.createElement("div", { key: i, title: line.feature, "aria-label": line.feature, "data-testid": `fmfv-event-${r.rowId}-${i}`, style: {
                                position: 'absolute',
                                left: line.startColumnIndex * COL_W,
                                top: line.top + 6,
                                width: line.width,
                                height: BAR_H,
                                background: line.colorName || '#29c7ee',
                                color: line.eventtextcolor || '#ffffff',
                                fontSize: 11,
                                lineHeight: `${BAR_H}px`,
                                borderRadius: 3,
                                padding: '0 6px',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            } }, line.feature)))));
                })))));
};
export default FMForecastView;
//# sourceMappingURL=FMForecastView.js.map